import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment.development';
import { ErrorHandlingService } from '../../../core/services/error-handling.service';
import { BehaviorSubject, catchError, Observable, map } from 'rxjs';
import { ReportLabel, ReportLabelPayload } from './report-labels.model';
import { ApiResponse } from '../../../core/models/api-response.models';
import {
  LabelDefaultValue,
  LabelKey,
} from '../../../core/models/report-labels.model';

@Injectable({
  providedIn: 'root',
})
export class ReportLabelsService {
  private readonly errorHandler = inject(ErrorHandlingService);
  private readonly BASE_URL = environment.apiUrl;
  private readonly OrganizationReportLabel = `${this.BASE_URL}/OrganizationReportLabel`;

  private reportLableSubject = new BehaviorSubject<ReportLabel[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  public reportLabels$ = this.reportLableSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {}

  loadReportLabels(orgId: number): void {
    this.loadingSubject.next(true);
    const url = `${this.OrganizationReportLabel}/get-org-report-label-list?OrganizationId=${orgId}`;
    this.http
      .get<ApiResponse<ReportLabel[]>>(url)
      .pipe(catchError(error => this.errorHandler.handleError(error)))
      .subscribe({
        next: response => {
          if (response.result) {
            this.reportLableSubject.next(response.result);
            this.loadingSubject.next(false);
          }
        },
        error: () => {
          console.error('Failed to load report labels');
          this.loadingSubject.next(false);
        },
      });
  }

  createReportLabel(
    reportLabel: ReportLabelPayload
  ): Observable<ApiResponse<ReportLabel>> {
    const url = `${this.OrganizationReportLabel}/create`;
    return this.http.post<ApiResponse<ReportLabel>>(url, reportLabel).pipe(
      map(response => {
        if (response.result) {
          this.updateLocalLabel(response.result);
        }
        return response;
      }),
      catchError(error => this.errorHandler.handleError(error))
    );
  }

  getLabelValue(key: LabelKey | string): Observable<string> {
    return this.reportLabels$.pipe(
      map(labels => {
        const label = labels.find(l => l.labelKey === key);
        return (
          label?.overrideValue ||
          LabelDefaultValue[key as keyof typeof LabelDefaultValue] ||
          key
        );
      })
    );
  }

  getLabelMap(): Observable<Record<string, string>> {
    return this.reportLabels$.pipe(
      map(labels => {
        return labels.reduce(
          (acc, label) => {
            acc[label.labelKey] = label.overrideValue || label.labelKey;
            return acc;
          },
          {} as Record<string, string>
        );
      })
    );
  }

  private updateLocalLabel(updatedLabel: ReportLabel): void {
    const currentLabels = this.reportLableSubject.getValue();
    const updatedLabels = currentLabels.map(label =>
      label.id === updatedLabel.id ? updatedLabel : label
    );

    if (!currentLabels.some(label => label.id === updatedLabel.id)) {
      updatedLabels.push(updatedLabel);
    }

    this.reportLableSubject.next(updatedLabels);
  }
}
